import { PensionProvidersName } from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { formatFullName, showCurrencyByCountry } from '@nxsys/utils';
import { pensionProviderIdOptions } from '@nxsys/constants/AppConstants';
import { formatMoney } from '@nxsys/utils/number-helper';
import { getShownPensionValue } from '@nxsys/utils/reported-pension';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { PENSION_CONTRIBUTION_TAB } from './PensionContributionReport';
import { Tooltip } from 'antd';
import { Payroll } from '@nxsys/store/slices/revenueFilings/types';

export const getAutoEnrollmentStatusColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<IPensionAssessed> => {
  return [
    {
      key: 'employeeId',
      title: messages['common.employeeId'],
      width: 200,
      dataIndex: 'subcontractor',
      render: (sub) => sub.id,
    },
    {
      key: 'employeeName',
      title: messages['common.name'],
      dataIndex: 'subcontractor',
      width: 250,
      render: (record) => formatFullName(record) || '_',
    },
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      width: 250,
      render: (record) => {
        const { subcontractor } = record;
        return `${subcontractor?.primaryAgency?.agencyPrefix || '___'}/${
          subcontractor?.agencyRef || subcontractor?.systemId || ''
        }`;
      },
    },
    {
      key: 'statePensionAgeDate',
      title: messages['common.statePensionAgeDate'],
      dataIndex: 'statePensionAgeDate',
      align: 'right',
      width: 200,
      render: (statePensionAgeDate) =>
        statePensionAgeDate
          ? moment(statePensionAgeDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'companyPayment',
      width: 300,
      render: (companyPayment) => companyPayment?.companyName || '_',
    },
    {
      key: 'pensionProvider',
      title: messages['common.pensionProvider'],
      dataIndex: 'pensionProviderId',
      width: 200,
      render: (pensionProviderId) => pensionProviderIdOptions.find(
        (provider) =>
          provider?.value === pensionProviderId,
      )?.label || '_'
      
    },
    {
      key: 'schemeId',
      title: messages['common.schemeId'],
      dataIndex: 'pensionSchemeId',
      width: 200,
      render: (pensionSchemeId) => pensionSchemeId || '_',
    },
    {
      key: 'schemeName',
      title: messages['common.schemeName'],
      dataIndex: 'pensionSchemeName',
      width: 200,
      render: (pensionSchemeName) => pensionSchemeName || '_',
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'assessStatus',
      align: 'center',
      width: 200,
      render: (assessStatus) => assessStatus || '_',
    },
    {
      key: 'lastAssessedOn',
      title: messages['common.lastAssessedOn'],
      align: 'right',
      width: 200,
      dataIndex: 'assessedAt',
      render: (assessedAt) =>
        assessedAt
          ? moment(assessedAt).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'category',
      title: messages['common.category'],
      align: 'center',
      dataIndex: 'category',
      width: 200,
      render: (category) => category || '_',
    },
    {
      key: 'changedOn',
      title: messages['common.changedOn'],
      dataIndex: 'categoryChangedAt',
      align: 'right',
      width: 200,
      render: (categoryChangedAt) =>
        categoryChangedAt
          ? moment(categoryChangedAt).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'postponementEndDate',
      title: messages['common.postponementEndDate'],
      align: 'right',
      width: 250,
      dataIndex: 'postponementEndDate',
      render: (postponementEndDate) =>
        postponementEndDate
          ? moment(postponementEndDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },

    {
      key: 'ejhDate',
      title: messages['common.ejhDate'],
      dataIndex: 'ejhDate',
      align: 'right',
      width: 200,
      render: (ejhDate) =>
        ejhDate
          ? moment(ejhDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'employeePercent',
      title: messages['common.employeePercent'],
      dataIndex: 'eePercent',
      align: 'right',
      width: 200,
      render: (value, { employeeContributionPercentage }) => {
        if (
          employeeContributionPercentage ||
          employeeContributionPercentage === 0
        ) {
          return `${employeeContributionPercentage}%`;
        }
        return value ? `${Number(value) * 100}%` : '_';
      },
    },

    {
      key: 'employerPercent',
      title: messages['common.employerPercent'],
      dataIndex: 'erPercent',
      align: 'right',
      width: 200,
      render: (value, { employerContributionPercentage }) => {
        if (
          employerContributionPercentage ||
          employerContributionPercentage === 0
        ) {
          return `${employerContributionPercentage}%`;
        }
        return value ? `${Number(value) * 100}%` : '_';
      },
    },

    {
      key: 'membershipId',
      title: messages['common.membershipId'],
      dataIndex: 'memberId',
      width: 200,
      render: (memberId) => memberId || '_',
    },

    {
      key: 'membershipStarted',
      title: messages['common.membershipStarted'],
      dataIndex: 'membershipStartDate',
      align: 'right',
      width: 250,
      render: (membershipStartDate) =>
        membershipStartDate
          ? moment(membershipStartDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'optInDate',
      title: messages['common.optInDate'],
      dataIndex: 'otpInDate',
      align: 'right',
      width: 200,
      render: (otpInDate) =>
        otpInDate
          ? moment(otpInDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'optOutDate',
      title: messages['common.optOutDate'],
      dataIndex: 'otpOutDate',
      align: 'right',
      width: 200,
      render: (otpOutDate) =>
        otpOutDate
          ? moment(otpOutDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'refundDue',
      title: messages['common.refundDueQuestion'],
      dataIndex: 'refundDue',
      align: 'center',
      width: 200,
      render: (refundDue) =>
        refundDue ? messages['common.yes'] : messages['common.no'],
    },
    {
      key: 'refundAmount',
      title: messages['common.refundAmount'],
      dataIndex: 'refundAmount',
      align: 'right',
      width: 200,
      render: (refundAmount) =>
        refundAmount
          ? formatMoney(refundAmount, showCurrencyByCountry(payrollCountry))
          : '_',
    },
    {
      key: 'refundEmployeePaid',
      title: messages['common.refundEmployeePaid'],
      // dataIndex: 'refundPaidEE',
      align: 'right',
      width: 200,
      render: (record) =>
        record?.isRefundPaid && record?.refundPaidEE
          ? formatMoney(
              record.refundPaidEE,
              showCurrencyByCountry(payrollCountry),
            )
          : '_',
    },
    {
      key: 'refundEmployerPaid',
      title: messages['common.refundEmployerPaid'],
      // dataIndex: 'refundPaidER',
      align: 'right',
      width: 200,
      render: (record) =>
        record?.isRefundPaid && record?.refundPaidER
          ? formatMoney(
              record.refundPaidER,
              showCurrencyByCountry(payrollCountry),
            )
          : '_',
    },
    {
      key: 'totalRefundPaid',
      title: messages['common.totalRefundPaid'],
      // dataIndex: 'refundPaidAmount',
      align: 'right',
      width: 200,
      render: (record) =>
        record?.isRefundPaid && record?.refundPaidAmount
          ? formatMoney(
              record.refundPaidAmount,
              showCurrencyByCountry(payrollCountry),
            )
          : '_',
    },
    {
      key: 'refundPaidDate',
      title: messages['common.refundPaidDate'],
      // dataIndex: 'refundPaidDate',
      align: 'right',
      width: 200,
      render: (record) =>
        record?.isRefundPaid && record?.refundPaidDate
          ? moment(record.refundPaidDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
    },
  ];
};

export const getPensionContributionReportColumns = ({
  messages,
  payrollCountry,
  pensionTabType,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  pensionTabType: PENSION_CONTRIBUTION_TAB;
  isBureau?: boolean;
}): ColumnsType<IPensionSubmission> => {
  return [
    ...((pensionTabType === PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION
      ? [
          {
            title: messages['table.error'],
            dataIndex: 'contributePensionError',
            width: 350,
            render: (value: any) => value || '_',
          },
        ]
      : []) as ColumnsType<IPensionSubmission>),
    // {
    //   title: messages['common.status'],
    //   dataIndex: 'status',
    //   align: 'center',
    //   render: (value, record) =>
    //     record?.expectDoneAt ? (
    //       <Tooltip
    //         title={`${messages['common.expectedCompletion']}: ${moment(
    //           record.expectDoneAt,
    //         ).format('DD/MM/YYYY HH:mm')}`}
    //       >
    //         <span>{value || '_'}</span>
    //       </Tooltip>
    //     ) : (
    //       <span>{value || '_'}</span>
    //     ),
    // },
    {
      title: messages['common.taxPeriod'],
      align: 'right',
      render: (record) => `${record?.taxPeriod || ''}/${record?.taxYear || ''}`,
    },
    {
      title: messages['common.pensionProvider'],
      render: (record) =>
        PensionProvidersName[
          record?.pensionProvider as keyof typeof PensionProvidersName
        ] || '_',
    },
    {
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorId',
      render: (value) => value || '_',
    },
    {
      title: messages['common.name'],
      dataIndex: 'subcontractor',
      render: (value) => formatFullName(value) || '_',
    },
    {
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      width: 200,
      render: (record) =>
        `${record?.agency?.agencyPrefix || '___'}/${
          record?.subcontractor?.agencyRef ||
          record?.subcontractor?.id ||
          '_'
        }`,
    },
    {
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'company',
      width: 200,
      render: (value) => value?.companyName || '_',
    },
    {
      title: messages['table.grossForPension'],
      align: 'right',
      dataIndex: 'grossForPension',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.employeeContribution'],
      dataIndex: 'eePension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },

    {
      title: messages['common.avcEmployee'],
      dataIndex: 'payroll',
      align: 'right',
      // erPension already includes the employee AVC under salary sacrifice, so
      // show the reported (0) value to avoid counting it twice.
      render: (value) =>
        formatMoney(
          getShownPensionValue(value, 'avcEEPension'),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.employerContribution'],
      dataIndex: 'erPension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },

    {
      title: messages['common.avcEmployer'],
      dataIndex: 'payroll',
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.payeeUmbrellaValue?.avcERPension,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.additionalVoluntaryContribution'],
      width: 300,
      align: 'right',
      dataIndex: 'avc',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.totalContribution'],
      dataIndex: 'contributedPension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];
};

export const getPendingPensionContributionReportColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<Payroll> => {
  return [
    {
      title: messages['table.status'],
      align: 'center',
      width: 150,
      render: (value) => {
        const waitingExpectDate = value?.pensionSubmission?.expectDoneAt
          ? moment(value?.pensionSubmission?.expectDoneAt).format(
              'DD/MM/YYYY HH:mm',
            )
          : '';
        return waitingExpectDate ? (
          <Tooltip title={`Will be done at: ${waitingExpectDate}`}>
            <span className="text-info">
              {value?.pensionSubmission?.statusMessage || '_'}
            </span>
          </Tooltip>
        ) : (
          <span className="text-info">
            {value?.pensionSubmission?.statusMessage || '_'}
          </span>
        );
      },
    },
    {
      title: messages['common.taxPeriod'],
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.selfBillRef?.taxYear || ''
        }`,
      width: 160,
    },
    {
      title: messages['common.pensionProvider'],
      render: (record) =>
        PensionProvidersName[
          record?.companyRef?.autoEnrolSettings
            ?.pensionProviderId as keyof typeof PensionProvidersName
        ] || '_',
    },
    {
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id || '_',
      width: 200,
    },
    {
      title: messages['common.name'],
      dataIndex: 'subcontractorRef',
      render: (value) => formatFullName(value) || '_',
    },
    {
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.id || '_'
        }`,
    },
    {
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'companyRef',
      width: 200,
      render: (value) => <span>{value?.companyName || '_'}</span>,
    },
    {
      title: messages['table.grossForPension'],
      align: 'right',
      dataIndex: 'payeeUmbrellaValue',
      render: (value) =>
        formatMoney(
          value?.grossForTaxAndNI,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.employeeContribution'],
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      // Pending contributions show the split the provider will receive: under
      // salary sacrifice EE and AVC EE are 0 and ER includes the sacrifice.
      render: (_, record) =>
        formatMoney(
          getShownPensionValue(record, 'employeePension'),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['common.avcEmployee'],
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      render: (_, record) =>
        formatMoney(
          getShownPensionValue(record, 'avcEEPension'),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.employerContribution'],
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (_, record) =>
        formatMoney(
          getShownPensionValue(record, 'employersPension'),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['common.avcEmployer'],
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      render: (value) =>
        formatMoney(value?.avcERPension, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.additionalVoluntaryContribution'],
      width: 300,
      align: 'right',
      dataIndex: 'payeeUmbrellaValue',
      render: (value) =>
        formatMoney(
          value?.additionalVoluntaryContribution,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.totalContribution'],
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.employeePension + value?.employersPension,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];
};
